import React, { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { Badge, Box, ButtonBase, Tooltip } from '@mui/material';
import {
  Assessment,
  CalendarMonth,
  Group,
  Home,
  MenuBook,
  MonitorHeart,
  Payment,
  Timeline
} from '@mui/icons-material';
import UserAvatar from 'components/shared/user/UserAvatar';
import SidebarDrawerContext from 'context/SidebarDrawerContext';
import { roles } from 'common/constants/options';
import routes from 'common/routes';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * Barra de navegación INFERIOR para móvil, al estilo de las apps sociales: solo iconos, sin
 * texto, y la foto de perfil en el último hueco.
 *
 * Por qué solo iconos: con etiqueta, cinco destinos no caben a 360 px sin que el texto se corte
 * o se apile en dos líneas. Los iconos elegidos son los mismos del sidebar, así que el usuario
 * los reconoce de una; el destino activo se marca en dorado, que es el único acento de la marca.
 *
 * Por qué la foto abre el SIDEBAR y no lleva al perfil: en la barra caben cinco cosas y el menú
 * tiene veinte. La foto es el acceso a "todo lo demás" (perfil, pagos, progreso, ajustes), que es
 * exactamente el gesto que la gente ya trae aprendido de otras apps.
 *
 * La barra existe para TODOS los roles a propósito: la hamburguesa de la barra superior se quitó,
 * así que esta foto es la ÚNICA puerta al menú en móvil. Un rol sin barra se quedaría sin
 * navegación.
 */
function MobileTabBar() {
  const { t } = useTranslation(['routes']);
  const { pathname } = useLocation();
  const { openMobileSidebar, mobileOpen } = useContext(SidebarDrawerContext);

  const user = useSelector((state) => state.auth.user);
  const isOrgManager = useSelector((state) => state.auth.user?.is_org_manager) === true;

  const role = user?.role;

  // Cuatro destinos por rol + la foto. Se eligen los que se usan A DIARIO; todo lo demás vive
  // detrás de la foto. La barra es la misma para todos los clientes, sea cual sea su canal: tener
  // un hueco que cambiaba de destino según el plan hacía que dos personas vieran barras distintas
  // y no pudieran ayudarse entre ellas.
  const items = useMemo(() => {
    const home = { key: 'home', icon: <Home />, label: t('home', { ns: 'routes' }) };
    const calendar = {
      key: 'calendar',
      icon: <CalendarMonth />,
      to: routes.calendar.path,
      label: t('calendar', { ns: 'routes', defaultValue: 'Agenda' })
    };
    // El gestor de empresa también necesita barra: al quitar la hamburguesa, su panel se quedaba
    // sin ninguna forma de abrir el menú en móvil.
    if (isOrgManager) {
      return [
        { ...home, to: routes.orgClientDashboard.path },
        {
          key: 'org-wellbeing',
          icon: <MonitorHeart />,
          to: routes.orgClientWellbeing.path,
          label: t('orgClient.wellbeing', { ns: 'routes', defaultValue: 'Bienestar' })
        },
        {
          key: 'org-members',
          icon: <Group />,
          to: routes.orgClientMembers.path,
          label: t('orgClient.members', { ns: 'routes', defaultValue: 'Colaboradores' })
        },
        {
          key: 'org-reports',
          icon: <Assessment />,
          to: routes.orgClientReports.path,
          label: t('orgClient.reports', { ns: 'routes', defaultValue: 'Reportes' })
        }
      ];
    }

    // Los dos últimos huecos cambiaron por pedido del cliente: en el día a día se consultan más
    // los clientes, los pagos y el seguimiento que la lista de sesiones o las noticias. Nada se
    // pierde, todo eso sigue en el menú que abre la foto.
    if (role === roles.trainer) {
      return [
        { ...home, to: routes.trainerDashboard.path },
        calendar,
        {
          key: 'client',
          icon: <Group />,
          to: routes.listClient.path,
          label: t('client.list', { ns: 'routes', defaultValue: 'Clientes' })
        },
        {
          key: 'performance',
          icon: <Assessment />,
          to: routes.listPerformance.path,
          label: t('performance.list', { ns: 'routes', defaultValue: 'Rendimientos' })
        }
      ];
    }

    // El supervisor se trata igual que el admin, como en el resto de la aplicación.
    if (role === roles.administrator || role === roles.supervisor) {
      return [
        { ...home, to: routes.adminDashboard.path },
        calendar,
        {
          key: 'payments',
          icon: <Payment />,
          to: routes.payments.path,
          label: t('payment.payments', { ns: 'routes', defaultValue: 'Pagos' })
        },
        {
          key: 'client',
          icon: <Group />,
          to: routes.listClient.path,
          label: t('client.list', { ns: 'routes', defaultValue: 'Clientes' })
        }
      ];
    }

    return [
      { ...home, to: routes.clientDashboard.path },
      calendar,
      {
        key: 'progress',
        icon: <Timeline />,
        to: routes.clientProgress.path,
        label: t('progress.myProgress', { ns: 'routes', defaultValue: 'Mi progreso' })
      },
      {
        key: 'my-program',
        icon: <MenuBook />,
        to: routes.clientProgram.path,
        label: t('clientProgram', { ns: 'routes', defaultValue: 'Mi Programa' })
      }
    ];
  }, [role, isOrgManager, t]);

  if (!user) return null;
  // Dentro del cascarón nativo (user agent OtrixApp) la barra inferior la pinta la app:
  // mostrar las dos a la vez era navegación duplicada.
  if (/OtrixApp/.test(window.navigator.userAgent)) return null;

  return (
    <Box
      component="nav"
      aria-label={t('mainMenu', { ns: 'routes', defaultValue: 'Menú principal' })}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        display: { xs: 'flex', lg: 'none' },
        alignItems: 'center',
        justifyContent: 'space-around',
        backgroundColor: 'surface.sidebar',
        borderTop: '1px solid',
        borderColor: 'hairline.main',
        // `env(safe-area-inset-bottom)`: sin esto, en los iPhone con barra de gestos los iconos
        // quedan debajo de la franja del sistema y no se pueden tocar.
        pb: 'env(safe-area-inset-bottom, 0px)',
        height: 'calc(56px + env(safe-area-inset-bottom, 0px))'
      }}>
      {items.map((item) => {
        // `startsWith` y no igualdad: al entrar al detalle de una sesión, su pestaña debe seguir
        // marcada. La raíz se compara exacta o marcaría todo.
        const active =
          item.to === '/' ? pathname === '/' : pathname === item.to || pathname.startsWith(`${item.to}/`);

        return (
          <Tooltip key={item.key} title={item.label}>
            <ButtonBase
              component={NavLink}
              to={item.to}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
              sx={{
                flex: 1,
                height: 56,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: active ? 'gold.main' : 'text.secondary',
                transition: `color 200ms ${EASE}, transform 200ms ${EASE}`,
                '&:active': { transform: 'scale(0.92)' },
                '& .MuiSvgIcon-root': { fontSize: 26 }
              }}>
              {item.icon}
            </ButtonBase>
          </Tooltip>
        );
      })}

      {/* La foto: puerta al menú completo. El punto dorado avisa de que ahí hay más cosas la
          primera vez; se apaga mientras el panel está abierto para no competir con él. */}
      <Tooltip title={t('mainMenu', { ns: 'routes', defaultValue: 'Menú principal' })}>
        <ButtonBase
          // El tour móvil apunta aquí ("Toca para abrir la navegación completa"). El ancla vivía
          // en la hamburguesa de la cabecera, que ya no existe: sin moverla, ese paso del tour se
          // quedaba señalando un elemento que no está en el DOM.
          data-tour="menu-toggle"
          onClick={openMobileSidebar}
          aria-label={t('mainMenu', { ns: 'routes', defaultValue: 'Menú principal' })}
          aria-haspopup="dialog"
          aria-expanded={mobileOpen ? 'true' : undefined}
          sx={{
            flex: 1,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: `transform 200ms ${EASE}`,
            '&:active': { transform: 'scale(0.92)' }
          }}>
          <Badge
            color="secondary"
            variant="dot"
            invisible={mobileOpen}
            sx={{ '& .MuiBadge-badge': { backgroundColor: 'gold.main' } }}>
            <Box
              sx={{
                display: 'inline-flex',
                borderRadius: '50%',
                border: '2px solid',
                borderColor: mobileOpen ? 'gold.main' : 'hairline.strong',
                transition: `border-color 200ms ${EASE}`
              }}>
              <UserAvatar size={28} textSize={13} user={user} />
            </Box>
          </Badge>
        </ButtonBase>
      </Tooltip>
    </Box>
  );
}

export default MobileTabBar;
